import React, { useState } from "react";
import {
  getProductId,
  getProductName,
  getProductWithCartQuantity,
} from "../../utils";

const FONT = "'Plus Jakarta Sans', sans-serif";
const T = {
  cta: "#557c2e",
  ink: "#23272f",
  muted: "#7c8593",
  line: "#eceef0",
  good: "#3f8f3a",
  chipbg: "#eef3e6",
};

const suggestionFormatter = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const CompactCard = ({
  product,
  isMobile,
  onAdd,
  onShowModal,
  isInWishlist,
  wishPending,
  onToggleFill,
}) => {
  const [added, setAdded] = useState(false);
  const id = getProductId(product);
  const name = getProductName(product);
  const img = product?.productImage || product?.boxImage;
  const price =
    product?.discountPrice || product?.price || product?.amount || 0;

  const handleAdd = () => {
    onAdd?.(getProductId(product));
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  const openModal = () => onShowModal?.(product);
  const clickable = { cursor: "pointer" };

  const heart = onToggleFill ? (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (!wishPending) onToggleFill(id);
      }}
      aria-label="wishlist"
      style={{
        position: "absolute",
        top: "6px",
        right: "6px",
        width: "26px",
        height: "26px",
        borderRadius: "50%",
        background: "#fff",
        border: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        boxShadow: "0 1px 3px rgba(0,0,0,.12)",
      }}
    >
      {wishPending ? (
        <span
          className="spinner-border"
          role="status"
          style={{
            width: "0.7rem",
            height: "0.7rem",
            borderWidth: "2px",
            color: T.cta,
          }}
        />
      ) : (
        <i
          className={`bi ${isInWishlist ? "bi-heart-fill" : "bi-heart"}`}
          style={{
            fontSize: "0.8rem",
            color: isInWishlist ? T.cta : "#c4c9cf",
          }}
        />
      )}
    </button>
  ) : null;

  const addStyle = {
    border: "none",
    background: added ? "#557c2e" : "#eef4e7",
    color: added ? "#fff" : "#557c2e",
    fontWeight: 800,
    cursor: "pointer",
    fontFamily: FONT,
  };

  if (!isMobile) {
    return (
      <div
        style={{
          flex: "0 0 150px",
          scrollSnapAlign: "start",
          background: "#fff",
          border: "1px solid #eceef0",
          borderRadius: "13px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ position: "relative" }}>
          <img
            src={img}
            alt={name}
            onClick={openModal}
            style={{
              height: "90px",
              width: "100%",
              objectFit: "cover",
              display: "block",
              ...clickable,
            }}
          />
          {heart}
        </div>
        <div style={{ padding: "9px" }}>
          <div
            onClick={openModal}
            style={{
              fontSize: "11.5px",
              fontWeight: 700,
              lineHeight: 1.25,
              height: "29px",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              color: "#23272f",
              ...clickable,
            }}
          >
            {name}
          </div>
          <div
            className="d-flex align-items-center justify-content-between"
            style={{ marginTop: "7px" }}
          >
            <span
              style={{ fontSize: "12.5px", fontWeight: 800, color: "#23272f" }}
            >
              {suggestionFormatter.format(price)}
            </span>
            <button
              onClick={handleAdd}
              style={{
                ...addStyle,
                fontSize: "11px",
                borderRadius: "8px",
                padding: "6px 10px",
              }}
            >
              {added ? "✓ Added" : "Add"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: "0 0 150px",
        scrollSnapAlign: "start",
        background: "#fff",
        border: "1px solid #eceef0",
        borderRadius: "15px",
        padding: "10px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ position: "relative", marginBottom: "9px" }}>
        <img
          src={img}
          alt={name}
          onClick={openModal}
          style={{
            height: "96px",
            width: "100%",
            objectFit: "cover",
            borderRadius: "11px",
            display: "block",
            ...clickable,
          }}
        />
        {heart}
      </div>
      <div
        onClick={openModal}
        style={{
          fontSize: "12.5px",
          fontWeight: 700,
          lineHeight: 1.3,
          height: "33px",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          color: "#23272f",
          ...clickable,
        }}
      >
        {name}
      </div>
      <div
        style={{
          fontSize: "14px",
          fontWeight: 800,
          marginTop: "8px",
          color: "#23272f",
        }}
      >
        {suggestionFormatter.format(price)}
      </div>
      <button
        onClick={handleAdd}
        className="d-flex align-items-center justify-content-center gap-1"
        style={{
          ...addStyle,
          fontSize: "13px",
          borderRadius: "10px",
          padding: "9px 0",
          marginTop: "auto",
        }}
      >
        {added ? "✓ Added" : "+ Add"}
      </button>
    </div>
  );
};

const FullCard = ({
  product,
  isMobile,
  isInWishlist,
  wishPending,
  onShowModal,
  onToggleFill,
  onAdd,
  onInc,
  onDec,
}) => {
  const id = getProductId(product);
  const name = getProductName(product);
  const img = product?.productImage || product?.boxImage;
  const isOOS =
    product?.stockLevel === "out_of_stock" || product?.quantityAvailable === 0;
  const price =
    product?.discountPrice || product?.price || product?.amount || 0;
  const qty = product?.quantity || 0;

  const addChip = {
    border: "none",
    background: T.chipbg,
    color: T.cta,
    fontWeight: 800,
    fontSize: isMobile ? "12px" : "12.5px",
    borderRadius: "9px",
    padding: isMobile ? "7px 12px" : "8px 14px",
    cursor: "pointer",
    fontFamily: FONT,
  };
  const stepBtn = {
    border: "none",
    background: "transparent",
    color: T.cta,
    fontWeight: 800,
    fontSize: "16px",
    cursor: "pointer",
    width: "18px",
    lineHeight: 1,
  };

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${T.line}`,
        borderRadius: "15px",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ position: "relative", height: "150px" }}>
        <img
          src={img}
          alt={name}
          onClick={() => onShowModal?.(product)}
          loading="lazy"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            cursor: "pointer",
          }}
        />
        {isOOS && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,.55)",
            }}
          />
        )}
        {!isMobile && !isOOS && product?.inSeason && (
          <span
            style={{
              position: "absolute",
              top: "10px",
              left: "10px",
              background: T.good,
              color: "#fff",
              fontSize: "10px",
              fontWeight: 800,
              padding: "4px 9px",
              borderRadius: "99px",
            }}
          >
            IN SEASON
          </span>
        )}
        {onToggleFill && (
          <button
            onClick={() => !wishPending && onToggleFill(id)}
            aria-label="wishlist"
            style={{
              position: "absolute",
              top: "9px",
              right: "9px",
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              background: "#fff",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            {wishPending ? (
              <span
                className="spinner-border"
                role="status"
                style={{
                  width: "0.8rem",
                  height: "0.8rem",
                  borderWidth: "2px",
                  color: T.cta,
                }}
              />
            ) : (
              <i
                className={`bi ${isInWishlist ? "bi-heart-fill" : "bi-heart"}`}
                style={{
                  fontSize: "0.95rem",
                  color: isInWishlist ? T.cta : "#c4c9cf",
                }}
              />
            )}
          </button>
        )}
      </div>

      <div
        style={{
          padding: isMobile ? "10px" : "13px",
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <div
          onClick={() => onShowModal?.(product)}
          style={{
            fontSize: isMobile ? "12.5px" : "13.5px",
            fontWeight: 700,
            lineHeight: 1.3,
            color: T.ink,
            minHeight: isMobile ? "32px" : "36px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            cursor: "pointer",
          }}
        >
          {name}
        </div>
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ marginTop: isMobile ? "8px" : "10px" }}
        >
          <span
            style={{
              fontSize: isMobile ? "13.5px" : "15px",
              fontWeight: 800,
              color: T.ink,
            }}
          >
            {suggestionFormatter.format(price)}
          </span>
          {isOOS ? (
            <button
              disabled
              style={{
                ...addChip,
                background: "#eef0f2",
                color: "#9aa1ab",
                cursor: "not-allowed",
              }}
            >
              Out of stock
            </button>
          ) : qty === 0 ? (
            <button onClick={() => onAdd?.(id)} style={addChip}>
              Add
            </button>
          ) : (
            <div
              className="d-flex align-items-center"
              style={{
                gap: "8px",
                background: T.chipbg,
                borderRadius: "9px",
                padding: "3px 8px",
              }}
            >
              <button onClick={() => onDec?.(id)} style={stepBtn}>
                −
              </button>
              <span
                style={{
                  fontWeight: 800,
                  fontSize: "13px",
                  color: T.ink,
                  minWidth: "14px",
                  textAlign: "center",
                }}
              >
                {qty}
              </span>
              <button onClick={() => onInc?.(id)} style={stepBtn}>
                +
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SuggestionCard = ({ variant = "compact", ...props }) =>
  variant === "full" ? <FullCard {...props} /> : <CompactCard {...props} />;

const SuggestedProducts = ({
  products = [],
  isMobile = false,
  onAdd,
  onShowModal,
  title = "You may also like",
  showSwipeHint = true,
  className = "",
  style,
  variant = "compact",
  cartItems = [],
  isInWishlist,
  isWishPending,
  onToggleFill,
  onInc,
  onDec,
}) => {
  if (!products || products.length === 0) return null;

  if (variant === "full") {
    return (
      <section className={className} style={style}>
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ marginBottom: "18px" }}
        >
          <h2
            style={{
              fontFamily: FONT,
              fontSize: isMobile ? "18px" : "22px",
              fontWeight: 800,
              color: T.ink,
              margin: 0,
            }}
          >
            {title}
          </h2>
          {showSwipeHint && (
            <span style={{ fontSize: "12px", color: T.muted, fontWeight: 600 }}>
              swipe →
            </span>
          )}
        </div>
        <div
          className="no-scrollbar d-flex"
          style={{
            gap: isMobile ? "12px" : "18px",
            overflowX: "auto",
            paddingBottom: "6px",
            scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {products.map((product) => {
            const displayProduct = getProductWithCartQuantity(
              product,
              cartItems,
            );
            const id = getProductId(product);
            return (
              <div
                key={id}
                style={{
                  flex: isMobile ? "0 0 150px" : "0 0 19%",
                  scrollSnapAlign: "start",
                }}
              >
                <FullCard
                  product={displayProduct}
                  isMobile={isMobile}
                  isInWishlist={isInWishlist ? isInWishlist(id) : false}
                  wishPending={isWishPending ? isWishPending(id) : false}
                  onShowModal={onShowModal}
                  onToggleFill={onToggleFill}
                  onAdd={onAdd}
                  onInc={onInc}
                  onDec={onDec}
                />
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  // Compact variant: original cart/checkout rail.
  return (
    <section className={className} style={style}>
      <div className="d-flex align-items-center justify-content-between px-1 px-md-0 mb-3">
        <h4
          className="mb-0"
          style={{ fontWeight: 800, fontSize: "16px", color: "#23272f" }}
        >
          {title}
        </h4>
        {showSwipeHint && (
          <span style={{ fontSize: "12px", color: "#7c8593", fontWeight: 600 }}>
            swipe →
          </span>
        )}
      </div>
      <div
        className="no-scrollbar d-flex"
        style={{
          gap: "11px",
          overflowX: "auto",
          paddingBottom: "6px",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {products.map((product) => (
          <CompactCard
            key={getProductId(product)}
            isInWishlist={
              isInWishlist ? isInWishlist(getProductId(product)) : false
            }
            wishPending={
              isWishPending ? isWishPending(getProductId(product)) : false
            }
            onToggleFill={onToggleFill}
            product={product}
            isMobile={isMobile}
            onAdd={onAdd}
            onShowModal={onShowModal}
          />
        ))}
      </div>
    </section>
  );
};

export default SuggestedProducts;
